import React, { useState, useEffect, useRef } from 'react';
import { Dropdown, Space, Tooltip, message } from 'antd';
import { DownOutlined, MenuOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sidebarNavigation, SidebarNavigationItem } from '../../sider/sidebarNavigation';
import { useResponsive } from '../../../../../hooks/useResponsive';
import { LanguageSwitcher } from '../../../../header/components/LanguageSwitcher/LanguageSwitcher';
import { useAuth } from '../../../../../contexts/AuthContext';
import * as S from './HorizontalNavigation.styles';

const HorizontalNavigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isTablet, mobileOnly } = useResponsive();
  const { isAuthenticated, isInitialized } = useAuth();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  
  const authTooltip = t('common:pleaseLogin');
  
  console.log('[HorizontalNavigation] Auth status:', { isAuthenticated, isInitialized });
  

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  const handleMenuClick = (url: string, requiresAuth: boolean = true) => {
    // Проверяем требует ли пункт авторизацию
    if (requiresAuth && (!isAuthenticated || !isInitialized)) {
      console.log('[HorizontalNavigation] Navigation blocked - requires auth');
      return; // Блокируем навигацию
    }
    navigate(url);
  };

  const getDropdownMenuItems = (children: SidebarNavigationItem[]) => ({
    items: children.map((child) => {
      const requiresAuth = child.requiresAuth !== false;
      const isDisabled = requiresAuth && (!isAuthenticated || !isInitialized);
      
      return {
        key: child.key,
        label: isDisabled ? (
          <Tooltip title={authTooltip}>
            <span style={{ color: 'rgba(0, 0, 0, 0.25)' }}>{t(child.title) || child.title}</span>
          </Tooltip>
        ) : (
          t(child.title) || child.title
        ),
        onClick: () => child.url && handleMenuClick(child.url, requiresAuth),
        disabled: isDisabled,
        style: isDisabled ? { cursor: 'not-allowed', opacity: 0.5 } : undefined,
      };
    })
  });

  const isActive = (item: SidebarNavigationItem): boolean => {
    if (item.url && location.pathname === item.url) return true;
    if (item.children) {
      return item.children.some(child => child.url === location.pathname);
    }
    return false;
  };

  const renderMobileMenuItem = (item: SidebarNavigationItem) => {
    const active = isActive(item);
    const requiresAuth = item.requiresAuth !== false;
    const isDisabled = requiresAuth && (!isAuthenticated || !isInitialized);
    
    const handleDisabledClick = () => {
      if (isDisabled) {
        message.warning(authTooltip);
      }
    };
    
    if (item.children && item.children.length > 0) {
      return (
        <div key={item.key}>
          <S.MobileMenuItem 
            $active={active}
            onClick={handleDisabledClick}
            style={isDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
          >
            {item.icon}
            <span>{t(item.title) || item.title}</span>
            {isDisabled && <span style={{ fontSize: '11px', color: '#999', marginLeft: 'auto' }}>🔒</span>}
          </S.MobileMenuItem>
          {item.children.map((child) => {
            const childRequiresAuth = child.requiresAuth !== false;
            const childIsDisabled = childRequiresAuth && (!isAuthenticated || !isInitialized);
            
            return (
              <S.MobileMenuItem
                key={child.key}
                $active={child.url === location.pathname}
                onClick={() => {
                  if (childIsDisabled) {
                    message.warning(authTooltip);
                    return;
                  }
                  if (child.url) {
                    handleMenuClick(child.url, childRequiresAuth);
                    setMobileMenuOpen(false);
                  }
                }}
                style={{ 
                  paddingLeft: '48px',
                  opacity: childIsDisabled ? 0.5 : 1,
                  cursor: childIsDisabled ? 'not-allowed' : 'pointer'
                }}
              >
                <span>{t(child.title) || child.title}</span>
                {childIsDisabled && <span style={{ fontSize: '11px', color: '#999', marginLeft: 'auto' }}>🔒</span>}
              </S.MobileMenuItem>
            );
          })}
        </div>
      );
    }

    return (
      <S.MobileMenuItem
        key={item.key}
        $active={active}
        onClick={() => {
          if (isDisabled) {
            message.warning(authTooltip);
            return;
          }
          if (item.url) {
            handleMenuClick(item.url, requiresAuth);
            setMobileMenuOpen(false);
          }
        }}
        style={isDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
      >
        {item.icon}
        <span>{t(item.title) || item.title}</span>
        {isDisabled && <span style={{ fontSize: '11px', color: '#999', marginLeft: 'auto' }}>🔒</span>}
      </S.MobileMenuItem>
    );
  };

  return (
    <S.NavigationContainer ref={menuRef}>
      <S.NavigationWrapper>
        {/* Mobile: Burger on left */}
        {mobileOnly && (
          <S.MobileMenuButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <MenuOutlined style={{ color: '#007bff', fontSize: '18px' }} />
          </S.MobileMenuButton>
        )}
        
        {/* Desktop: Full menu */}
        {!mobileOnly && (
          <S.MenuSection>
            {sidebarNavigation.map((item) => {
              const active = isActive(item);
              const requiresAuth = item.requiresAuth !== false;
              const isDisabled = requiresAuth && (!isAuthenticated || !isInitialized);
              
              if (item.children && item.children.length > 0) {
                const menuItemContent = (
                  <S.MenuItem
                    $active={active}
                    $hovered={hoveredItem === item.key}
                    onMouseEnter={() => setHoveredItem(item.key)}
                    onMouseLeave={() => setHoveredItem(null)}
                    style={isDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                  >
                    <Space>
                      {item.icon}
                      <span>{t(item.title) || item.title}</span>
                      <DownOutlined style={{ fontSize: '12px' }} />
                    </Space>
                  </S.MenuItem>
                );

                return isDisabled ? (
                  <Tooltip key={item.key} title={authTooltip}>
                    {menuItemContent}
                  </Tooltip>
                ) : (
                  <Dropdown
                    key={item.key}
                    menu={getDropdownMenuItems(item.children)}
                    trigger={['hover']}
                    placement="bottomCenter"
                  >
                    {menuItemContent}
                  </Dropdown>
                );
              }

              const menuItemContent = (
                <S.MenuItem
                  key={item.key}
                  $active={active}
                  $hovered={hoveredItem === item.key}
                  onClick={() => item.url && handleMenuClick(item.url, requiresAuth)}
                  onMouseEnter={() => setHoveredItem(item.key)}
                  onMouseLeave={() => setHoveredItem(null)}
                  style={isDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                >
                  <Space>
                    {item.icon}
                    <span>{t(item.title) || item.title}</span>
                  </Space>
                </S.MenuItem>
              );

              return isDisabled ? (
                <Tooltip key={item.key} title={authTooltip}>
                  {menuItemContent}
                </Tooltip>
              ) : menuItemContent;
            })}
          </S.MenuSection>
        )}

        {/* Mobile: Spacer to push language button to right */}
        {mobileOnly && <div style={{ flex: 1 }} />}

        {/* Mobile: Language button on right */}
        {mobileOnly && (
          <S.MobileRightSection>
            <LanguageSwitcher variant="dark" />
          </S.MobileRightSection>
        )}
      </S.NavigationWrapper>
      
      <S.MobileMenu $isOpen={mobileMenuOpen}>
        {sidebarNavigation.map(renderMobileMenuItem)}
      </S.MobileMenu>
    </S.NavigationContainer>
  );
};

export default HorizontalNavigation;
